import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  Text,
  Checkbox,
  Button,
  Banner,
  Divider,
  ChoiceList,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { MENU_ITEMS, enumerateMenuKeys } from "../../utility/MenuConfig";

function MenuSettings() {
  const { session, claims, menuPrefs, reloadMenuPrefs, hasPermission, setNavLayout } = UserAuth();
  const role = claims?.role;
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedBanner, setSavedBanner] = useState(false);

  useEffect(() => {
    setPrefs(menuPrefs || {});
  }, [menuPrefs]);

  // Hide role-restricted menus from users who can't see them anyway.
  const visibleMenuItems = MENU_ITEMS.filter(
    (item) => !item.requiresRole || role === item.requiresRole
  );
  const visibleKeys = new Set();
  for (const item of visibleMenuItems) {
    visibleKeys.add(item.key);
    if (item.submenu)
      item.submenu
        .filter((s) => !s.requiresPermission || hasPermission(s.requiresPermission))
        .forEach((s) => visibleKeys.add(s.key));
  }
  const rows = enumerateMenuKeys().filter((r) => visibleKeys.has(r.key));

  const getPref = (key) => prefs[key] || {};
  const isVisible = (key) => getPref(key).visible !== false;
  const areNotificationsOn = (key) => getPref(key).notifications !== false;

  const setVisible = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      [key]: { ...prev[key], visible: value },
    }));
  };

  const setNotifications = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      [key]: { ...prev[key], notifications: value },
    }));
  };

  const save = async () => {
    if (!session?.user?.id) return;
    setSaving(true);
    setSavedBanner(false);
    const { error } = await supabase
      .from("users")
      .update({ menu_preferences: prefs })
      .eq("id", session.user.id);
    setSaving(false);
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    await reloadMenuPrefs();
    setSavedBanner(true);
  };

  // Group rows by top-level menu so the UI reads like the sidebar itself.
  const topLevelItems = visibleMenuItems.map((item) => ({
    parent: item,
    rows: rows.filter((r) => r.key === item.key || r.parent === item.key),
  }));

  return (
    <AppWrapper>
      <Page
        title="Personalizar menú"
        backAction={{ content: "Volver", url: "/dashboard" }}
        primaryAction={
          <Button variant="primary" onClick={save} loading={saving}>
            Guardar
          </Button>
        }
      >
        <BlockStack gap="400">
          {savedBanner && (
            <Banner tone="success" onDismiss={() => setSavedBanner(false)}>
              <p>Preferencias guardadas.</p>
            </Banner>
          )}
          <Card>
            <ChoiceList
              title="Diseño del menú"
              choices={[
                { label: "Clásico", value: "classic", helpText: "Barra de iconos con submenús al pasar el cursor." },
                { label: "Nuevo", value: "polaris", helpText: "Menú con nombres, agrupado por secciones." },
              ]}
              selected={[menuPrefs?._layout === "polaris" ? "polaris" : "classic"]}
              onChange={([v]) => setNavLayout(v)}
            />
          </Card>
          <Card>
            <BlockStack gap="400">
              <Text variant="bodyMd" tone="subdued">
                Elige qué elementos aparecen en tu barra lateral. Algunos
                permiten activar notificaciones con indicadores dinámicos.
              </Text>
              {topLevelItems.map(({ parent, rows: groupRows }, i) => (
                <div key={parent.key}>
                  {i > 0 && <Divider />}
                  <BlockStack gap="300">
                    {groupRows.map((row) => {
                      const indent = row.parent ? 24 : 0;
                      const hasNotifications = !!row.notificationSource;
                      const notifId = `notif-${row.key}`;
                      const notifChecked =
                        isVisible(row.key) && areNotificationsOn(row.key);
                      const notifDisabled = !isVisible(row.key);
                      return (
                        <div
                          key={row.key}
                          style={{ paddingLeft: indent }}
                        >
                          <Checkbox
                            label={row.label}
                            checked={isVisible(row.key)}
                            onChange={(val) => setVisible(row.key, val)}
                          />
                          {hasNotifications && (
                            <label
                              htmlFor={notifId}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                marginTop: 4,
                                marginLeft: 24,
                                fontSize: "0.75rem",
                                lineHeight: 1,
                                color: notifDisabled
                                  ? "#d9a2a4"
                                  : "#d72c0d",
                                cursor: notifDisabled ? "default" : "pointer",
                              }}
                            >
                              <input
                                id={notifId}
                                type="checkbox"
                                checked={notifChecked}
                                disabled={notifDisabled}
                                onChange={(e) =>
                                  setNotifications(row.key, e.target.checked)
                                }
                                style={{
                                  appearance: "auto",
                                  WebkitAppearance: "checkbox",
                                  MozAppearance: "checkbox",
                                  width: 14,
                                  height: 14,
                                  margin: 0,
                                  accentColor: "#d72c0d",
                                  verticalAlign: "middle",
                                }}
                              />
                              Notificaciones
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </BlockStack>
                </div>
              ))}
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default MenuSettings;
